import { useMutation } from "@tanstack/react-query";

import { useApplication } from "@olist/microfrontend";

import { TicketDataQueryKeys } from "./queryKeys";
import { supportTicketsService } from "./services";
import { ECreateCommentDto } from "types/support-tickets";
import { queryClient } from "utils/react-query";

interface IUseCreateCommentParams {
  id: string;
  comment: ECreateCommentDto;
}

interface IGetTicketAttachmentParams {
  id: string;
}

export function useTicketComment({
  onSuccess: onSuccessParam,
}: {
  onError: () => void;
  onSuccess: () => void;
}) {
  const context = useApplication();
  const accessToken = context?.accessToken || "";

  const { isError, isPending, isSuccess, mutate } = useMutation({
    mutationFn: ({
      comment,
      id,
    }: Omit<IUseCreateCommentParams, "accessToken">) =>
      supportTicketsService.createComment(id, comment, accessToken),
    retry: 3,
    onMutate: async () => {
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.DETAILS],
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.DETAILS],
      });
    },
    onSuccess: () => {
      onSuccessParam?.();
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.DETAILS],
      });
    },
  });

  return {
    createComment: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
  };
}

export function useGetTicketAttachment() {
  const context = useApplication();
  const accessToken = context?.accessToken || "";

  const { isError, isPending, isSuccess, mutate } = useMutation({
    mutationFn: ({ id }: Omit<IGetTicketAttachmentParams, "accessToken">) =>
      supportTicketsService.fetchTicketAttachment(accessToken, id),
    retry: 1,
    mutationKey: [TicketDataQueryKeys.ATTACHMENT],
    gcTime: 30000,
    onMutate: async () => {
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.ATTACHMENT],
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.ATTACHMENT],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TicketDataQueryKeys.ATTACHMENT],
      });
    },
  });

  return {
    getAttachment: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
  };
}
